import { extraerBase64DeDataUri } from './dataUri';

export const ANCHO_MAXIMO_DOCUMENTO_PX = 1000;
// deuda: calidad por defecto del WebP/JPEG, publicada sin calibrar con boletas reales. techo: sirve
// mientras monto, fecha y folio se lean sin zoom en un teléfono (criterio 5 del spec); si no se leen,
// hay que subir el valor, a costa de más peso por foto. disparador: cuando haya al menos 10 boletas
// reales cargadas, mezclando térmicas y de talonario (la medición solo lee comprobantes, no
// modifica ninguno; ver STATE.md).
export const CALIDAD_COMPROBANTE = 0.72;
export const MAX_BYTES_FOTO_ORIGINAL = 10 * 1024 * 1024;
export const MAX_BYTES_PDF = 2 * 1024 * 1024;
export const MAX_BYTES_PAGINA_PROCESADA = 1024 * 1024;
export const MAX_PAGINAS_COMPROBANTE = 5;
/** Lado largo máximo antes de editar: Safari en iOS no dibuja lienzos de más de ~16,7 millones de puntos. */
const LADO_MAXIMO_EDICION_PX = 4000;

const TIPOS_IMAGEN = new Set(['image/jpeg', 'image/png', 'image/webp']);

export type ClaseArchivo = 'imagen' | 'heic' | 'pdf' | 'no_admitido';

export function clasificarArchivo(archivo: File): ClaseArchivo {
  if (archivo.type === 'application/pdf' || /\.pdf$/i.test(archivo.name)) return 'pdf';
  if (archivo.type === 'image/heic' || archivo.type === 'image/heif' || /\.(heic|heif)$/i.test(archivo.name)) return 'heic';
  if (TIPOS_IMAGEN.has(archivo.type)) return 'imagen';
  return 'no_admitido';
}

export function cargarImagen(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => { resolve(img); };
    img.onerror = () => { reject(new Error('No pudimos leer la imagen.')); };
    img.src = src;
  });
}

function canvasABlob(canvas: HTMLCanvasElement, tipo: string, calidad: number): Promise<Blob | null> {
  return new Promise((resolve) => { canvas.toBlob(resolve, tipo, calidad); });
}

function nuevoCanvas(ancho: number, alto: number): { canvas: HTMLCanvasElement; ctx: CanvasRenderingContext2D } {
  const canvas = document.createElement('canvas');
  canvas.width = ancho;
  canvas.height = alto;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No pudimos procesar la imagen.');
  ctx.imageSmoothingQuality = 'high';
  return { canvas, ctx };
}

async function comoBlobIntermedio(canvas: HTMLCanvasElement): Promise<Blob> {
  const blob = await canvasABlob(canvas, 'image/jpeg', 0.92);
  if (!blob) throw new Error('No pudimos procesar la imagen.');
  return blob;
}

/** Deja la foto lista para el paso de ajuste: HEIC convertida, orientación aplicada y tamaño editable. */
export async function prepararFotoParaAjuste(archivo: File): Promise<Blob> {
  let origen: Blob = archivo;
  if (clasificarArchivo(archivo) === 'heic') {
    const heic2any = (await import('heic2any')).default;
    origen = await heic2any({ blob: archivo, toType: 'image/jpeg', quality: 0.92 }) as Blob;
  }
  const url = URL.createObjectURL(origen);
  try {
    // Los navegadores actuales aplican la orientación EXIF al dibujar: la foto de lado queda derecha.
    const img = await cargarImagen(url);
    const escala = Math.min(1, LADO_MAXIMO_EDICION_PX / Math.max(img.naturalWidth, img.naturalHeight));
    const { canvas, ctx } = nuevoCanvas(Math.round(img.naturalWidth * escala), Math.round(img.naturalHeight * escala));
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
    return await comoBlobIntermedio(canvas);
  } finally {
    URL.revokeObjectURL(url);
  }
}

export type Giro = 90 | 180 | 270;

/** Gira la imagen completa en sentido horario. */
export async function girarImagen(img: HTMLImageElement, giro: Giro): Promise<Blob> {
  const cuartoDeVuelta = giro === 90 || giro === 270;
  const ancho = cuartoDeVuelta ? img.naturalHeight : img.naturalWidth;
  const alto = cuartoDeVuelta ? img.naturalWidth : img.naturalHeight;
  const { canvas, ctx } = nuevoCanvas(ancho, alto);
  ctx.translate(ancho / 2, alto / 2);
  ctx.rotate((giro * Math.PI) / 180);
  ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
  return comoBlobIntermedio(canvas);
}

/** Rectángulo en puntos reales de la imagen (no de la pantalla). */
export interface Recorte {
  readonly x: number;
  readonly y: number;
  readonly ancho: number;
  readonly alto: number;
}

async function codificarComprobante(canvas: HTMLCanvasElement): Promise<Blob> {
  const webp = await canvasABlob(canvas, 'image/webp', CALIDAD_COMPROBANTE);
  if (webp?.type === 'image/webp') return webp;
  // Safari no codifica WebP y devuelve PNG, que en una boleta pesa varias veces más: JPEG.
  const jpeg = await canvasABlob(canvas, 'image/jpeg', CALIDAD_COMPROBANTE);
  if (!jpeg) throw new Error('No pudimos procesar la imagen.');
  return jpeg;
}

/** Recorta (opcional), limita el ancho del documento a 1.000 px y codifica. */
export async function procesarPagina(img: HTMLImageElement, recorte: Recorte | null): Promise<Blob> {
  const zona = recorte ?? { x: 0, y: 0, ancho: img.naturalWidth, alto: img.naturalHeight };
  const escala = Math.min(1, ANCHO_MAXIMO_DOCUMENTO_PX / zona.ancho);
  const { canvas, ctx } = nuevoCanvas(Math.round(zona.ancho * escala), Math.round(zona.alto * escala));
  ctx.drawImage(img, zona.x, zona.y, zona.ancho, zona.alto, 0, 0, canvas.width, canvas.height);
  return codificarComprobante(canvas);
}

/** Solo el payload base64, nunca el data URI crudo (ver dataUri.ts y PR #198/#200). */
export function blobABase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const lector = new FileReader();
    lector.onloadend = () => {
      if (typeof lector.result === 'string') resolve(extraerBase64DeDataUri(lector.result));
      else reject(new Error('No pudimos leer el archivo.'));
    };
    lector.onerror = () => { reject(new Error('No pudimos leer el archivo.')); };
    lector.readAsDataURL(blob);
  });
}

/** Detección best-effort de un PDF con contraseña: el diccionario /Encrypt aparece en el archivo. */
export async function pdfProtegido(archivo: File): Promise<boolean> {
  return (await archivo.text()).includes('/Encrypt');
}
